import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import type { KnowledgeNode } from "../types"
import { useToast } from "@/hooks"
import { ErrorCode } from "@/constants/errors"
import { DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE } from "@/constants/billing"
import {
  FetchStatus,
  useCancelWebsiteCrawlTargetApi,
  useGetBillingSubscriptionApi,
  useGetWebsiteCrawlFailuresApi,
  useGetWebsiteCrawlStateApi,
  useSaveWebsiteCrawlTargetApi,
  useSyncWebsiteCrawlTargetApi,
  transformBillingOverview,
  type BillingOverview,
  type WebsiteCrawlFailureSchema,
  type WebsiteCrawlFailureStatus,
  type WebsiteCrawlJobSchema,
  type WebsiteCrawlTargetSchema,
} from "@/hooks/api"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
  normalizeApiError,
} from "@/lib/apiErrors"

export interface WebsiteCrawlFormState {
  startUrl: string
  maxDepth: number
  limit: number
  includePaths: string
  excludePaths: string
  allowSubdomains: boolean
  ignoreQueryParameters: boolean
  ignoreSitemap: boolean
  onlyMainContent: boolean
}

const DEFAULT_WEBSITE_CRAWL_FORM: WebsiteCrawlFormState = {
  startUrl: "",
  maxDepth: 3,
  limit: 100,
  includePaths: "",
  excludePaths: "/login,/logout,/cart,/checkout,/account,/admin",
  allowSubdomains: false,
  ignoreQueryParameters: true,
  ignoreSitemap: true,
  onlyMainContent: true,
}

const ACTIVE_JOB_STATUSES = new Set(["PENDING", "RUNNING"])
const FAILURE_STATUSES: WebsiteCrawlFailureStatus[] = [
  "DOWNLOAD_FAILED",
  "FAILED",
]

const toPathArray = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0)

const normalizePathScope = (value: string): string | null => {
  const normalized = `/${value.trim().replace(/^\/+|\/+$/g, "")}`
  return normalized === "/" ? null : normalized
}

const extractRequiredCredits = (message?: string): number | null => {
  const match = message?.match(/Required\s+([0-9]+(?:\.[0-9]+)?)/i)
  if (!match) return null
  const value = Number(match[1])
  return Number.isFinite(value) ? value : null
}

const estimateReservedCredits = (pageLimit: number, creditRate: number) =>
  Math.ceil(Math.max(0, pageLimit * creditRate) - Number.EPSILON)

const normalizeStartUrlForSubmission = (
  rawStartUrl: string
): { rootStartUrl: string; pathScope: string | null } => {
  const trimmed = rawStartUrl.trim()
  if (!trimmed) return { rootStartUrl: "", pathScope: null }

  const hasExplicitScheme = /^https?:\/\//i.test(trimmed)
  try {
    const parsed = new URL(hasExplicitScheme ? trimmed : `https://${trimmed}`)
    const pathScope = normalizePathScope(parsed.pathname)
    const rootStartUrl = hasExplicitScheme
      ? `${parsed.protocol}//${parsed.host}`
      : parsed.host
    return { rootStartUrl, pathScope }
  } catch {
    return { rootStartUrl: trimmed, pathScope: null }
  }
}

const buildSubmissionPayload = (value: WebsiteCrawlFormState) => {
  const { rootStartUrl, pathScope } = normalizeStartUrlForSubmission(
    value.startUrl
  )
  const includePaths = toPathArray(value.includePaths)
  if (pathScope && !includePaths.includes(pathScope)) {
    includePaths.push(pathScope)
  }

  return {
    startUrl: rootStartUrl,
    crawlConfig: {
      maxDepth: value.maxDepth,
      limit: value.limit,
      includePaths,
      excludePaths: toPathArray(value.excludePaths),
      allowSubdomains: value.allowSubdomains,
      ignoreQueryParameters: value.ignoreQueryParameters,
      ignoreSitemap: value.ignoreSitemap,
      onlyMainContent: value.onlyMainContent,
    },
  }
}

interface UseWebsiteCrawlerDialogParams {
  currentFolderNode: KnowledgeNode
  currentOrganizationId?: string
  currentWorkspaceId?: string
  isCurrentFolderCrawlerEligible: boolean
  refreshFolderChildren: (folderId: string) => Promise<KnowledgeNode[] | null>
  createWebsiteCrawl: (
    workspaceNodeId: string,
    payload: {
      startUrl: string
      crawlConfig?: Record<string, unknown>
    }
  ) => Promise<KnowledgeNode | null>
}

export interface UseWebsiteCrawlerDialogReturn {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  openDialog: () => void
  form: WebsiteCrawlFormState
  updateForm: (patch: Partial<WebsiteCrawlFormState>) => void
  target: WebsiteCrawlTargetSchema | null
  stateStatus: "loading" | "success" | "error"
  stateError: unknown
  refreshState: () => Promise<WebsiteCrawlTargetSchema | null>
  job: WebsiteCrawlJobSchema | null
  jobFolderId: string | null
  isJobActive: boolean
  isDirty: boolean
  canSave: boolean
  canSync: boolean
  isSaving: boolean
  isSyncing: boolean
  isCanceling: boolean
  failureDetails: WebsiteCrawlFailureSchema[]
  failureDetailsTotal: number
  failureDetailsStatus: WebsiteCrawlFailureStatus
  isFailureDetailsOpen: boolean
  isLoadingFailureDetails: boolean
  websiteCrawlCreditRatePerPage: number
  availableCredits: number | null
  billingOverview: BillingOverview | null
  refreshBillingOverview: () => Promise<WebsiteCrawlBillingSnapshot>
  showFailureDetails: (status: WebsiteCrawlFailureStatus) => Promise<void>
  setFailureDetailsOpen: (isOpen: boolean) => void
  save: () => Promise<void>
  sync: () => Promise<void>
  cancel: () => Promise<void>
  isCreateDialogOpen: boolean
  setIsCreateDialogOpen: (isOpen: boolean) => void
  openCreateDialog: () => void
  createForm: WebsiteCrawlFormState
  updateCreateForm: (patch: Partial<WebsiteCrawlFormState>) => void
  isCreatingWebsiteCrawl: boolean
  canCreateWebsiteCrawl: boolean
  createFromWorkspace: (
    workspaceNodeId: string
  ) => Promise<KnowledgeNode | null>
}

export interface WebsiteCrawlBillingSnapshot {
  websiteCrawlCreditRatePerPage: number
  availableCredits: number | null
}

export function useWebsiteCrawlerDialog({
  currentFolderNode,
  currentOrganizationId,
  currentWorkspaceId,
  isCurrentFolderCrawlerEligible,
  refreshFolderChildren,
  createWebsiteCrawl,
}: UseWebsiteCrawlerDialogParams): UseWebsiteCrawlerDialogReturn {
  const { t } = useTranslation()
  const { alertToast } = useToast()

  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState<WebsiteCrawlFormState>(
    DEFAULT_WEBSITE_CRAWL_FORM
  )
  const [baselineForm, setBaselineForm] = useState<WebsiteCrawlFormState>(
    DEFAULT_WEBSITE_CRAWL_FORM
  )
  const [target, setTarget] = useState<WebsiteCrawlTargetSchema | null>(null)
  const [stateStatus, setStateStatus] = useState<
    "loading" | "success" | "error"
  >("loading")
  const [stateError, setStateError] = useState<unknown>(null)
  const scopeKey = JSON.stringify([
    currentOrganizationId,
    currentWorkspaceId,
    currentFolderNode.id,
  ])
  const [stateScope, setStateScope] = useState(scopeKey)
  const stateRequestRef = useRef(0)
  const activeStateRequestRef = useRef<number | null>(null)
  const [job, setJob] = useState<WebsiteCrawlJobSchema | null>(null)
  const [jobFolderId, setJobFolderId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const hasUserEditedFormRef = useRef(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState<WebsiteCrawlFormState>(
    DEFAULT_WEBSITE_CRAWL_FORM
  )
  const [isCreatingWebsiteCrawl, setIsCreatingWebsiteCrawl] = useState(false)
  const [failureDetails, setFailureDetails] = useState<
    WebsiteCrawlFailureSchema[]
  >([])
  const [failureDetailsTotal, setFailureDetailsTotal] = useState(0)
  const [failureDetailsStatus, setFailureDetailsStatus] =
    useState<WebsiteCrawlFailureStatus>("DOWNLOAD_FAILED")
  const [isFailureDetailsOpen, setFailureDetailsOpen] = useState(false)
  const lastRefreshedJobKeyRef = useRef<string | null>(null)
  const saveRequestedRef = useRef(false)
  const syncRequestedRef = useRef(false)
  const cancelRequestedRef = useRef(false)

  useEffect(() => {
    setStateScope(scopeKey)
    setStateStatus("loading")
    setStateError(null)
    setTarget(null)
    setJob(null)
    setJobFolderId(null)
    saveRequestedRef.current = false
    syncRequestedRef.current = false
    cancelRequestedRef.current = false
    hasUserEditedFormRef.current = false
    setForm(DEFAULT_WEBSITE_CRAWL_FORM)
    setBaselineForm(DEFAULT_WEBSITE_CRAWL_FORM)
    return () => {
      stateRequestRef.current += 1
      activeStateRequestRef.current = null
    }
  }, [scopeKey])

  const { fetchFn: getWebsiteCrawlState } = useGetWebsiteCrawlStateApi()
  const { data: billingOverviewResp, fetchFn: getBillingOverview } =
    useGetBillingSubscriptionApi()
  const {
    status: getWebsiteCrawlFailuresStatus,
    fetchFn: getWebsiteCrawlFailures,
  } = useGetWebsiteCrawlFailuresApi()
  const {
    error: saveWebsiteCrawlTargetError,
    data: saveWebsiteCrawlTargetResp,
    fetchFn: saveWebsiteCrawlTarget,
  } = useSaveWebsiteCrawlTargetApi()
  const {
    error: syncWebsiteCrawlTargetError,
    data: syncWebsiteCrawlTargetResp,
    fetchFn: syncWebsiteCrawlTarget,
  } = useSyncWebsiteCrawlTargetApi()
  const {
    error: cancelWebsiteCrawlTargetError,
    data: cancelWebsiteCrawlTargetResp,
    fetchFn: cancelWebsiteCrawlTarget,
  } = useCancelWebsiteCrawlTargetApi()

  const updateForm = useCallback((patch: Partial<WebsiteCrawlFormState>) => {
    hasUserEditedFormRef.current = true
    setForm((prev) => ({ ...prev, ...patch }))
  }, [])

  const updateCreateForm = useCallback(
    (patch: Partial<WebsiteCrawlFormState>) => {
      setCreateForm((prev) => ({ ...prev, ...patch }))
    },
    []
  )

  const openDialog = useCallback(() => {
    setStateStatus("loading")
    setTarget(null)
    setJob(null)
    setJobFolderId(null)
    setFailureDetails([])
    setFailureDetailsTotal(0)
    setFailureDetailsOpen(false)
    setForm(DEFAULT_WEBSITE_CRAWL_FORM)
    setBaselineForm(DEFAULT_WEBSITE_CRAWL_FORM)
    hasUserEditedFormRef.current = false
    setIsOpen(true)
  }, [])

  const openCreateDialog = useCallback(() => {
    setCreateForm(DEFAULT_WEBSITE_CRAWL_FORM)
    setIsCreateDialogOpen(true)
  }, [])

  const isJobActive = useMemo(
    () => !!(job && ACTIVE_JOB_STATUSES.has(job.status)),
    [job]
  )

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baselineForm),
    [baselineForm, form]
  )

  const canSave = useMemo(
    () =>
      !!form.startUrl.trim() &&
      !!target &&
      isDirty &&
      !isSaving &&
      !isSyncing &&
      !isCanceling,
    [form.startUrl, target, isDirty, isSaving, isSyncing, isCanceling]
  )

  const canSync = useMemo(
    () =>
      !!form.startUrl.trim() &&
      !!target &&
      !isDirty &&
      !isSaving &&
      !isSyncing &&
      !isCanceling &&
      !isJobActive,
    [
      form.startUrl,
      target,
      isDirty,
      isSaving,
      isSyncing,
      isCanceling,
      isJobActive,
    ]
  )

  const canCreateWebsiteCrawl = useMemo(() => {
    return !!createForm.startUrl.trim() && !isCreatingWebsiteCrawl
  }, [createForm.startUrl, isCreatingWebsiteCrawl])

  const isLoadingFailureDetails =
    getWebsiteCrawlFailuresStatus === FetchStatus.Loading

  const billingOverview = useMemo(
    () =>
      billingOverviewResp?.data
        ? transformBillingOverview(billingOverviewResp.data)
        : null,
    [billingOverviewResp?.data]
  )
  const websiteCrawlCreditRatePerPage =
    billingOverview?.websiteCrawlCreditRatePerPage ??
    DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE
  const availableCredits = billingOverview
    ? billingOverview.creditsBalance + billingOverview.extraCreditsBalance
    : null

  const buildBillingSnapshot = useCallback(
    (overview: BillingOverview | null): WebsiteCrawlBillingSnapshot => ({
      websiteCrawlCreditRatePerPage:
        overview?.websiteCrawlCreditRatePerPage ??
        DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE,
      availableCredits: overview
        ? overview.creditsBalance + overview.extraCreditsBalance
        : null,
    }),
    []
  )

  const refreshBillingOverview =
    useCallback(async (): Promise<WebsiteCrawlBillingSnapshot> => {
      if (!currentOrganizationId) return buildBillingSnapshot(billingOverview)

      try {
        const resp = await getBillingOverview({
          searchParams: new URLSearchParams({
            organization_id: currentOrganizationId,
          }),
          preserveData: true,
        })
        return buildBillingSnapshot(
          resp?.data ? transformBillingOverview(resp.data) : billingOverview
        )
      } catch (error) {
        console.error("[KB] refresh billing overview failed", error)
        return buildBillingSnapshot(billingOverview)
      }
    }, [
      billingOverview,
      buildBillingSnapshot,
      currentOrganizationId,
      getBillingOverview,
    ])

  const targetToForm = useCallback((value: WebsiteCrawlTargetSchema) => {
    const cfg = value.crawl_config
    return {
      startUrl: value.start_url || "",
      maxDepth:
        typeof cfg?.maxDepth === "number" && Number.isFinite(cfg.maxDepth)
          ? Math.max(1, cfg.maxDepth)
          : DEFAULT_WEBSITE_CRAWL_FORM.maxDepth,
      limit:
        typeof cfg?.limit === "number" && Number.isFinite(cfg.limit)
          ? Math.max(10, cfg.limit)
          : DEFAULT_WEBSITE_CRAWL_FORM.limit,
      includePaths: Array.isArray(cfg?.includePaths)
        ? cfg.includePaths
            .map((item) => String(item || "").trim())
            .filter((item) => item.length > 0)
            .join(",")
        : DEFAULT_WEBSITE_CRAWL_FORM.includePaths,
      excludePaths: Array.isArray(cfg?.excludePaths)
        ? cfg.excludePaths
            .map((item) => String(item || "").trim())
            .filter((item) => item.length > 0)
            .join(",")
        : DEFAULT_WEBSITE_CRAWL_FORM.excludePaths,
      allowSubdomains:
        typeof cfg?.allowSubdomains === "boolean"
          ? cfg.allowSubdomains
          : DEFAULT_WEBSITE_CRAWL_FORM.allowSubdomains,
      ignoreQueryParameters:
        typeof cfg?.ignoreQueryParameters === "boolean"
          ? cfg.ignoreQueryParameters
          : DEFAULT_WEBSITE_CRAWL_FORM.ignoreQueryParameters,
      ignoreSitemap:
        typeof cfg?.ignoreSitemap === "boolean"
          ? cfg.ignoreSitemap
          : DEFAULT_WEBSITE_CRAWL_FORM.ignoreSitemap,
      onlyMainContent:
        typeof cfg?.onlyMainContent === "boolean"
          ? cfg.onlyMainContent
          : DEFAULT_WEBSITE_CRAWL_FORM.onlyMainContent,
    } satisfies WebsiteCrawlFormState
  }, [])

  const fetchCurrentState =
    useCallback(async (): Promise<WebsiteCrawlTargetSchema | null> => {
      if (!currentOrganizationId || !currentWorkspaceId) return null
      if (activeStateRequestRef.current !== null) return null

      const requestId = ++stateRequestRef.current
      activeStateRequestRef.current = requestId
      setStateStatus((previous) =>
        previous === "error" ? "loading" : previous
      )
      let resp
      try {
        resp = await getWebsiteCrawlState({
          urlBuilderParams: {
            organizationId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
            folderId: currentFolderNode.id,
          },
        })
      } catch (error) {
        if (requestId !== stateRequestRef.current) return null
        setStateError(error)
        setStateStatus("error")
        return null
      } finally {
        if (activeStateRequestRef.current === requestId) {
          activeStateRequestRef.current = null
        }
      }
      if (requestId !== stateRequestRef.current || !resp) return null
      setStateStatus("success")
      setStateError(null)

      const nextTarget = resp?.data?.target || null
      const nextJob = nextTarget?.latest_job || null
      setTarget(nextTarget)
      setJob(nextJob)
      setJobFolderId(nextJob ? currentFolderNode.id : null)

      if (nextTarget && !hasUserEditedFormRef.current) {
        const formFromTarget = targetToForm(nextTarget)
        setForm(formFromTarget)
        setBaselineForm(formFromTarget)
        hasUserEditedFormRef.current = false
        return nextTarget
      }

      if (!nextTarget && !hasUserEditedFormRef.current) {
        setForm(DEFAULT_WEBSITE_CRAWL_FORM)
        setBaselineForm(DEFAULT_WEBSITE_CRAWL_FORM)
      }

      return nextTarget
    }, [
      currentFolderNode.id,
      currentOrganizationId,
      currentWorkspaceId,
      getWebsiteCrawlState,
      targetToForm,
    ])

  const save = useCallback(async () => {
    if (
      !isCurrentFolderCrawlerEligible ||
      !currentWorkspaceId ||
      !currentOrganizationId ||
      !target
    ) {
      return
    }

    const startUrl = form.startUrl.trim()
    if (!startUrl) return
    const submission = buildSubmissionPayload(form)

    setIsSaving(true)
    saveRequestedRef.current = true
    try {
      await saveWebsiteCrawlTarget({
        urlBuilderParams: {
          organizationId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          targetId: target.id.toString(),
        },
        jsonBody: {
          start_url: submission.startUrl,
          crawl_config: submission.crawlConfig,
        },
      })
    } catch (error) {
      console.error("[KB] save website crawl target failed", error)
    } finally {
      setIsSaving(false)
    }
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    form,
    isCurrentFolderCrawlerEligible,
    saveWebsiteCrawlTarget,
    target,
  ])

  const sync = useCallback(async () => {
    if (
      !isCurrentFolderCrawlerEligible ||
      !currentWorkspaceId ||
      !currentOrganizationId ||
      !target
    ) {
      return
    }

    setIsSyncing(true)
    syncRequestedRef.current = true
    try {
      await syncWebsiteCrawlTarget({
        urlBuilderParams: {
          organizationId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          targetId: target.id.toString(),
        },
      })
    } catch (error) {
      console.error("[KB] sync website crawl target failed", error)
    } finally {
      setIsSyncing(false)
    }
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    isCurrentFolderCrawlerEligible,
    syncWebsiteCrawlTarget,
    target,
  ])

  const cancel = useCallback(async () => {
    if (!currentOrganizationId || !currentWorkspaceId || !target) return

    setIsCanceling(true)
    cancelRequestedRef.current = true
    try {
      await cancelWebsiteCrawlTarget({
        urlBuilderParams: {
          organizationId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          targetId: target.id.toString(),
        },
      })
    } catch (error) {
      console.error("[KB] cancel website crawl target failed", error)
    } finally {
      setIsCanceling(false)
    }
  }, [
    cancelWebsiteCrawlTarget,
    currentOrganizationId,
    currentWorkspaceId,
    target,
  ])

  const showFailureDetails = useCallback(
    async (status: WebsiteCrawlFailureStatus) => {
      if (
        !currentOrganizationId ||
        !currentWorkspaceId ||
        !job ||
        !FAILURE_STATUSES.includes(status)
      ) {
        return
      }

      setFailureDetailsStatus(status)
      setFailureDetailsOpen(true)
      try {
        const resp = await getWebsiteCrawlFailures({
          urlBuilderParams: {
            organizationId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
            jobId: job.id.toString(),
          },
          searchParams: new URLSearchParams({
            status,
            limit: "50",
          }),
          preserveData: true,
        })
        setFailureDetails(resp?.data?.items || [])
        setFailureDetailsTotal(resp?.data?.total || 0)
      } catch (error) {
        console.error("[KB] fetch website crawl failures failed", error)
        alertToast({
          title: t("knowledgeBase.treeView.websiteCrawlFailuresLoadFailed"),
          description: getUserFacingApiErrorMessage(
            error,
            t,
            t("constants.errors.somethingWrong")
          ),
        })
      }
    },
    [
      alertToast,
      currentOrganizationId,
      currentWorkspaceId,
      getWebsiteCrawlFailures,
      job,
      t,
    ]
  )

  useEffect(() => {
    if (!saveRequestedRef.current) return
    if (saveWebsiteCrawlTargetError) {
      alertToast({
        title: t("knowledgeBase.treeView.websiteCrawlSaveFailed"),
        description: getUserFacingApiErrorMessage(
          saveWebsiteCrawlTargetError,
          t,
          t("constants.errors.somethingWrong")
        ),
      })
      saveRequestedRef.current = false
      return
    }
    const savedTarget = saveWebsiteCrawlTargetResp?.data
    if (!savedTarget) return
    setTarget(savedTarget)
    const savedForm = targetToForm(savedTarget)
    setForm(savedForm)
    setBaselineForm(savedForm)
    hasUserEditedFormRef.current = false
    alertToast({
      title: t("knowledgeBase.treeView.websiteCrawlSaved"),
      description: t("knowledgeBase.treeView.websiteCrawlSavedDescription"),
    })
    saveRequestedRef.current = false
  }, [
    alertToast,
    saveWebsiteCrawlTargetError,
    saveWebsiteCrawlTargetResp?.data,
    t,
    targetToForm,
  ])

  useEffect(() => {
    if (!syncRequestedRef.current) return
    if (syncWebsiteCrawlTargetError) {
      const normalizedError = normalizeApiError(syncWebsiteCrawlTargetError)
      const errorData = normalizedError.errorData
      const isInsufficientCredits =
        normalizedError.errorCode === ErrorCode.ORG_BUDGET_INSUFFICIENT
      const requiredCredits =
        extractRequiredCredits(normalizedError.message ?? undefined) ??
        estimateReservedCredits(form.limit, websiteCrawlCreditRatePerPage)
      alertToast({
        title: isInsufficientCredits
          ? t("knowledgeBase.treeView.websiteCrawlInsufficientCreditsTitle")
          : t("knowledgeBase.treeView.websiteCrawlSyncFailed"),
        description: isInsufficientCredits
          ? t(
              "knowledgeBase.treeView.websiteCrawlInsufficientCreditsDescription",
              {
                credits: new Intl.NumberFormat(undefined, {
                  maximumFractionDigits: 2,
                }).format(requiredCredits),
              }
            )
          : normalizedError.messageKey
            ? t(normalizedError.messageKey)
            : getUserFacingApiErrorMessage(
                syncWebsiteCrawlTargetError,
                t,
                errorData?.message || t("constants.errors.somethingWrong")
              ),
      })
      syncRequestedRef.current = false
      return
    }
    const syncedJob = syncWebsiteCrawlTargetResp?.data
    if (!syncedJob) return
    setJob(syncedJob)
    setJobFolderId(currentFolderNode.id)
    alertToast({
      title: t("knowledgeBase.treeView.websiteCrawlSyncStarted"),
      description: t(
        "knowledgeBase.treeView.websiteCrawlSyncStartedDescription"
      ),
    })
    syncRequestedRef.current = false
  }, [
    alertToast,
    currentFolderNode.id,
    syncWebsiteCrawlTargetError,
    syncWebsiteCrawlTargetResp?.data,
    form.limit,
    t,
    websiteCrawlCreditRatePerPage,
  ])

  useEffect(() => {
    if (!cancelRequestedRef.current) return
    if (cancelWebsiteCrawlTargetError) {
      alertToast({
        title: t("knowledgeBase.treeView.websiteCrawlSyncFailed"),
        description: getUserFacingApiErrorMessage(
          cancelWebsiteCrawlTargetError,
          t,
          t("constants.errors.somethingWrong")
        ),
      })
      cancelRequestedRef.current = false
      return
    }
    const canceledJob = cancelWebsiteCrawlTargetResp?.data
    if (!canceledJob) return
    setJob(canceledJob)
    setJobFolderId(currentFolderNode.id)
    cancelRequestedRef.current = false
  }, [
    alertToast,
    currentFolderNode.id,
    cancelWebsiteCrawlTargetError,
    cancelWebsiteCrawlTargetResp?.data,
    t,
  ])

  const createFromWorkspace = useCallback(
    async (workspaceNodeId: string): Promise<KnowledgeNode | null> => {
      const startUrl = createForm.startUrl.trim()
      if (!workspaceNodeId || !startUrl || isCreatingWebsiteCrawl) return null
      const submission = buildSubmissionPayload(createForm)

      setIsCreatingWebsiteCrawl(true)
      try {
        const createdNode = await createWebsiteCrawl(workspaceNodeId, {
          startUrl: submission.startUrl,
          crawlConfig: submission.crawlConfig,
        })
        if (!createdNode) return null
        setCreateForm(DEFAULT_WEBSITE_CRAWL_FORM)
        setIsCreateDialogOpen(false)
        alertToast({
          title: t("knowledgeBase.treeView.websiteCrawlSaved"),
          description: t("knowledgeBase.treeView.websiteCrawlSavedDescription"),
        })
        return createdNode
      } catch (error) {
        console.error("[KB] create website crawl failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("knowledgeBase.treeView.websiteCrawlSaveFailed"),
            fallbackDescription: t(
              "knowledgeBase.treeView.websiteCrawlSaveFailed"
            ),
          }),
        })
        return null
      } finally {
        setIsCreatingWebsiteCrawl(false)
      }
    },
    [alertToast, createForm, createWebsiteCrawl, isCreatingWebsiteCrawl, t]
  )

  useEffect(() => {
    if (!isOpen && currentFolderNode.source_type !== "WEBSITE_CRAWLER") return
    if (
      !isCurrentFolderCrawlerEligible ||
      !currentWorkspaceId ||
      !currentOrganizationId
    ) {
      return
    }

    const run = async () => {
      try {
        await fetchCurrentState()
      } catch (error) {
        console.error("[KB] fetch website crawl state failed", error)
      }
    }

    void run()
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    currentFolderNode.id,
    currentFolderNode.source_type,
    isCurrentFolderCrawlerEligible,
    isOpen,
    fetchCurrentState,
  ])

  useEffect(() => {
    if (!isJobActive || jobFolderId !== currentFolderNode.id) return
    if (
      !isCurrentFolderCrawlerEligible ||
      !currentWorkspaceId ||
      !currentOrganizationId
    ) {
      return
    }

    const timerId = window.setInterval(() => {
      void fetchCurrentState().catch((error) => {
        console.error("[KB] fetch website crawl state failed", error)
      })
    }, 2500)

    return () => window.clearInterval(timerId)
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    currentFolderNode.id,
    isCurrentFolderCrawlerEligible,
    isJobActive,
    jobFolderId,
    fetchCurrentState,
  ])

  useEffect(() => {
    if (!job || jobFolderId !== currentFolderNode.id) return
    if (ACTIVE_JOB_STATUSES.has(job.status)) {
      lastRefreshedJobKeyRef.current = null
      return
    }

    const refreshKey = `${job.id}:${job.status}:${job.finished_at || ""}`
    if (lastRefreshedJobKeyRef.current === refreshKey) return
    lastRefreshedJobKeyRef.current = refreshKey

    void refreshFolderChildren(currentFolderNode.id).catch((error) => {
      console.error(
        "[KB] refresh folder children after crawl terminal status failed",
        error
      )
    })
  }, [currentFolderNode.id, job, jobFolderId, refreshFolderChildren])

  return {
    isOpen,
    setIsOpen,
    openDialog,
    form,
    updateForm,
    target: stateScope === scopeKey ? target : null,
    stateStatus: stateScope === scopeKey ? stateStatus : "loading",
    stateError: stateScope === scopeKey ? stateError : null,
    refreshState: fetchCurrentState,
    job: stateScope === scopeKey ? job : null,
    jobFolderId: stateScope === scopeKey ? jobFolderId : null,
    isJobActive,
    isDirty,
    canSave,
    canSync,
    isSaving,
    isSyncing,
    isCanceling,
    failureDetails,
    failureDetailsTotal,
    failureDetailsStatus,
    isFailureDetailsOpen,
    isLoadingFailureDetails,
    websiteCrawlCreditRatePerPage,
    availableCredits,
    billingOverview,
    refreshBillingOverview,
    showFailureDetails,
    setFailureDetailsOpen,
    save,
    sync,
    cancel,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    openCreateDialog,
    createForm,
    updateCreateForm,
    isCreatingWebsiteCrawl,
    canCreateWebsiteCrawl,
    createFromWorkspace,
  }
}
